import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Download, Link2, Loader2, Pencil, Plus, Trash2, Upload, X } from "lucide-react";
import api, { apiErrorMessage } from "../../lib/api";
import { formatBytes } from "../../lib/portalFiles";
import { uploadErrorText, uploadFile } from "../../lib/portalUpload";
import {
  acceptFor, appliedText, assignmentFacts, groupsLine, hoursText, linksFromText, linksToText, runningBehind, slotProblem,
  uploadLabel, versionLabel,
} from "../../lib/courseLibrary";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { FormattedText, FormattedTextarea } from "../portal/FormattedText";

const card = "rounded-2xl border border-[#E5E7EB] bg-white";
const quiet = "inline-flex items-center gap-1.5 rounded-full border border-[#E5E7EB] bg-white px-3 py-1.5 text-xs font-semibold text-[#373A45] hover:border-[#965EA7] hover:text-[#965EA7] disabled:opacity-50";
const field = "w-full rounded-xl border border-[#E5E7EB] bg-white px-3 py-2 text-sm text-[#373A45] focus:border-[#965EA7] focus:outline-none focus:ring-2 focus:ring-[#965EA7]/20";
const LESSON_KINDS = ["presentation", "support", "workshop", "assignment"];

/**
 * The course library (28.10.2026, plan PR 1): the standard content of the
 * course, per lesson, put in once by the team. Nothing here reaches a group
 * yet — that comes with the next steps of the plan.
 */
export default function CourseLibrary({ onBack }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null); // {kind, lesson} for a new one, or the item

  const load = useCallback(() => {
    api.get("/admin/portal/library").then((r) => setData(r.data))
      .catch((err) => setError(apiErrorMessage(err, "Nu am putut deschide biblioteca.")));
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="grid gap-6" data-testid="course-library">
      <button type="button" onClick={onBack} className="inline-flex w-fit items-center gap-1.5 text-sm text-[#6B7280] hover:text-[#965EA7]">
        <ArrowLeft size={14} /> Toate grupele
      </button>
      <div>
        <h2 className="font-display text-2xl font-bold text-[#373A45]">Biblioteca cursului</h2>
        <p className="mt-1 max-w-3xl text-sm text-[#6B7280]">
          Conținutul standard al cursului, pe lecții: prezentarea (PowerPoint pentru formatori, PDF pentru cursanți),
          suportul de curs, activitățile din sesiunile live și tema, precompletată. O grupă cu „Biblioteca cursului” le
          primește când i se deschide portalul, iar cursanții le văd la începutul sesiunii lecției. Fiecare schimbare e
          o versiune nouă: grupele în curs o primesc doar când apeși „Aplică în grupele în curs”.
        </p>
      </div>
      {error && <p className="text-sm text-[#c0483f]">{error}</p>}
      {!data && !error && <Loader2 className="mx-auto animate-spin text-[#6B7280]" />}
      {data && (
        <>
          <section className={`${card} p-5`} data-testid="library-assignments">
            <h3 className="font-semibold text-[#373A45]">Temele</h3>
            <p className={`mt-0.5 text-sm ${data.assignments.hours_ok ? "text-[#2F6B4E]" : "text-[#8a5d12]"}`}
              data-testid="library-hours">
              Însumează <strong>{hoursText(data.assignments.total_hours)}</strong> din{" "}
              {hoursText(data.assignments.required_hours)} asincron cerute de acreditare.
            </p>
            {data.assignments.warnings.length > 0 && (
              <ul className="mt-2 grid gap-1 text-xs text-[#8a5d12]">
                {data.assignments.warnings.map((w) => <li key={w}>{w}</li>)}
              </ul>
            )}
          </section>

          <section className={`${card} p-5`} data-testid="library-course">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <h3 className="font-semibold text-[#373A45]">Pentru tot cursul</h3>
                <p className="text-xs text-[#6B7280]">Notele pentru trainer: doar formatorii le văd, de la început.</p>
              </div>
              <button type="button" className={quiet} onClick={() => setEditing({ kind: "trainer_notes", lesson: null })}>
                <Plus size={13} /> Notele pentru trainer
              </button>
            </div>
            <Items items={data.course} data={data} onChanged={setData} onEdit={setEditing} reload={load} />
          </section>

          {data.lessons.map((les) => (
            <section key={les.n} className={`${card} p-5`} data-testid={`library-lesson-${les.n}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-[#7A4986]">Lecția {les.n}</p>
                  <h3 className="font-semibold text-[#373A45]">{les.name}</h3>
                  {les.missing.length > 0 && (
                    <p className="mt-0.5 text-xs text-[#8a5d12]">Lipsește: {les.missing.join(", ").toLowerCase()}.</p>
                  )}
                  {les.notes.map((n) => <p key={n} className="mt-0.5 text-xs text-[#8a5d12]">{n}</p>)}
                </div>
                <div className="flex flex-wrap gap-2">
                  {LESSON_KINDS.map((k) => (
                    <button key={k} type="button" className={quiet} onClick={() => setEditing({ kind: k, lesson: les.n })}
                      data-testid={`library-add-${k}-${les.n}`}>
                      <Plus size={13} /> {data.kinds[k].label}
                    </button>
                  ))}
                </div>
              </div>
              <Items items={les.items} data={data} onChanged={setData} onEdit={setEditing} reload={load} />
            </section>
          ))}
        </>
      )}
      {editing && (
        <ItemDialog editing={editing} data={data} onClose={() => setEditing(null)}
          onSaved={(next) => { setData(next); setEditing(null); }} />
      )}
    </div>
  );
}

function Items({ items, data, onChanged, onEdit, reload }) {
  if (!items.length) return <p className="mt-3 text-sm text-[#6B7280]">Nimic încă.</p>;
  return (
    <ul className="mt-3 grid gap-3">
      {items.map((it) => <Item key={it.id} item={it} data={data} onChanged={onChanged} onEdit={onEdit} reload={reload} />)}
    </ul>
  );
}

function Item({ item, data, onChanged, onEdit, reload }) {
  const [busy, setBusy] = useState("");
  const inputs = useRef({});
  const behind = runningBehind(item, data.running_groups);
  const applyRunning = async () => {
    if (!window.confirm(`Aplici versiunea v${item.version} a materialului „${item.title}” în grupele în curs? `
      + "La o temă la care s-a predat deja, orele rămân cum erau.")) return;
    setBusy("apply");
    try {
      const r = await api.post(`/admin/portal/library/items/${item.id}/apply-running`);
      toast.success(appliedText(r.data.result));
      onChanged(r.data);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu am putut aplica schimbarea."));
    } finally {
      setBusy("");
    }
  };

  const upload = async (audience, file) => {
    if (!file) return;
    const problem = slotProblem(item.kind, audience, file);
    if (problem) {
      toast.error(problem);
      if (inputs.current[audience]) inputs.current[audience].value = "";
      return;
    }
    setBusy(audience);
    try {
      await uploadFile(`/admin/portal/library/items/${item.id}/uploads`, file,
        { completeBase: "/admin/portal/library/files", extra: { audience } });
      toast.success(`${file.name} e încărcat.`);
      reload();
    } catch (err) {
      toast.error(uploadErrorText(err, apiErrorMessage));
    } finally {
      setBusy("");
      if (inputs.current[audience]) inputs.current[audience].value = "";
    }
  };
  const removeFile = async (f) => {
    if (!window.confirm(`Scoți „${f.name}” din material? Rămâne în versiunile de dinainte.`)) return;
    try { onChanged((await api.delete(`/admin/portal/library/items/${item.id}/files/${f.id}`)).data); }
    catch (err) { toast.error(apiErrorMessage(err, "Nu am putut scoate fișierul.")); }
  };
  const download = async (f) => {
    try { window.open((await api.get(`/admin/portal/library/files/${f.id}/download`)).data.url, "_blank", "noopener"); }
    catch (err) { toast.error(apiErrorMessage(err, "Nu am putut descărca fișierul.")); }
  };
  const archive = async () => {
    if (!window.confirm(`Scoți „${item.title}” din bibliotecă?`)) return;
    try { onChanged((await api.delete(`/admin/portal/library/items/${item.id}`)).data); }
    catch (err) { toast.error(apiErrorMessage(err, "Nu am putut scoate materialul.")); }
  };

  return (
    <li className="rounded-xl border border-[#E5E7EB] p-4" data-testid={`library-item-${item.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-[#6B7280]">{item.kind_label}</p>
          <p className="font-semibold text-[#373A45]">{item.title}</p>
          {item.kind === "assignment" && (
            <div className="mt-1 flex flex-wrap gap-1" data-testid={`library-facts-${item.id}`}>
              {assignmentFacts(item, data.assignments.component_labels).map((f) => (
                <span key={f} className="rounded-full bg-[#F0E7F4] px-2 py-0.5 text-[11px] font-semibold text-[#7A4986]">{f}</span>
              ))}
            </div>
          )}
          {item.description && (
            <div className="mt-1 text-sm text-[#373A45]">
              {item.kind === "assignment" && <p className="font-semibold">Cerința:</p>}
              <FormattedText text={item.description} className="mt-1" />
            </div>
          )}
          <p className="mt-1 text-xs text-[#6B7280]" title={item.versions.map(versionLabel).join("\n")}>
            {versionLabel(item.versions[0] || item)}
            {item.in_groups?.length > 0 && ` · în grupe: ${groupsLine(item.in_groups)}`}
          </p>
          {behind && (
            <button type="button" className={`${quiet} mt-2`} onClick={applyRunning} disabled={Boolean(busy)}
              data-testid={`library-apply-${item.id}`}>
              {busy === "apply" && <Loader2 size={13} className="animate-spin" />} Aplică în grupele în curs
            </button>
          )}
        </div>
        <div className="flex gap-1">
          <button type="button" onClick={() => onEdit(item)} aria-label="Editează" title="Editează"
            className="rounded-full p-1.5 text-[#6B7280] hover:bg-[#F0E7F4] hover:text-[#7A4986]"><Pencil size={14} /></button>
          <button type="button" onClick={archive} aria-label="Scoate din bibliotecă" title="Scoate din bibliotecă"
            className="rounded-full p-1.5 text-[#6B7280] hover:bg-[#FDECEC] hover:text-[#a33a32]"><Trash2 size={14} /></button>
        </div>
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        {item.audiences.map((aud) => {
          const mine = item.files.filter((f) => f.audience === aud);
          return (
            <div key={aud} className="min-w-0 rounded-lg bg-[#F7F5F9] p-3">
              <p className="text-xs font-semibold text-[#373A45]">{data.audience_labels[aud]}</p>
              {mine.length === 0 ? <p className="mt-1 text-xs text-[#6B7280]">Niciun fișier.</p> : (
                <ul className="mt-1 grid gap-1">
                  {mine.map((f) => (
                    <li key={f.id} className="flex items-start gap-2 text-sm">
                      <button type="button" onClick={() => download(f)} className="inline-flex min-w-0 items-start gap-1.5 text-left hover:text-[#965EA7]">
                        <Download size={13} className="mt-1 shrink-0" /> <span className="min-w-0 [overflow-wrap:anywhere]">{f.name}</span>
                      </button>
                      <span className="mt-0.5 shrink-0 whitespace-nowrap text-xs text-[#6B7280]">{formatBytes(f.size)}</span>
                      <button type="button" onClick={() => removeFile(f)} aria-label={`Scoate ${f.name}`}
                        className="ml-auto shrink-0 rounded p-0.5 text-[#9CA3AF] hover:text-[#a33a32]"><Trash2 size={12} /></button>
                    </li>
                  ))}
                </ul>
              )}
              <label className={`${quiet} mt-2 cursor-pointer`}>
                {busy === aud ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                {uploadLabel(item.kind, aud)}
                <input type="file" className="sr-only" disabled={Boolean(busy)} accept={acceptFor(item.kind, aud)}
                  ref={(el) => { inputs.current[aud] = el; }}
                  onChange={(e) => upload(aud, e.target.files?.[0])} data-testid={`library-upload-${aud}-${item.id}`} />
              </label>
            </div>
          );
        })}
      </div>

      {item.links.length > 0 && (
        <ul className="mt-3 grid gap-1">
          {item.links.map((l) => (
            <li key={l.url}>
              <a href={l.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-[#965EA7] hover:underline">
                <Link2 size={13} /> {l.label || l.url}
              </a>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

function ItemDialog({ editing, data, onClose, onSaved }) {
  const isNew = !editing.id;
  const kind = editing.kind;
  const [title, setTitle] = useState(isNew ? defaultTitle(editing, data) : editing.title);
  const [description, setDescription] = useState(isNew ? "" : editing.description);
  const [links, setLinks] = useState(isNew ? "" : linksToText(editing.links));
  const lessonHours = editing.lesson ? (data.lessons.find((x) => x.n === editing.lesson)?.practice_async ?? 1) : 1;
  const [task, setTask] = useState(() => ({
    hours: isNew ? lessonHours : editing.hours,
    component: isNew ? "practical" : editing.component,
    due_days: isNew ? data.assignments.due_days_default : editing.due_days,
    required: isNew ? true : editing.required,
    hard_close: isNew ? false : editing.hard_close,
  }));
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState({}); // audience -> files chosen here, uploaded on save
  const [progress, setProgress] = useState("");
  const isTask = kind === "assignment";
  const audiences = data.kinds[kind].audiences;

  const pick = (audience, list) => {
    const ok = [];
    for (const file of Array.from(list || [])) {
      const problem = slotProblem(kind, audience, file);
      if (problem) toast.error(problem);
      else ok.push(file);
    }
    if (ok.length) setPicked((p) => ({ ...p, [audience]: [...(p[audience] || []), ...ok] }));
  };
  const unpick = (audience, i) => setPicked((p) => ({ ...p, [audience]: p[audience].filter((_, j) => j !== i) }));

  // The material is saved first, then its files go up one by one; a file
  // that fails leaves the material saved, to finish from its card.
  const save = async () => {
    setBusy(true);
    const body = { title, description, links: linksFromText(links),
      ...(isTask ? { ...task, hours: Number(String(task.hours).replace(",", ".")), due_days: Number(task.due_days) } : {}) };
    let next;
    let id = editing.id;
    try {
      const r = isNew
        ? await api.post("/admin/portal/library/items", { ...body, kind, lesson: editing.lesson })
        : await api.put(`/admin/portal/library/items/${editing.id}`, body);
      next = r.data;
      if (isNew) id = r.data.item_id;
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu am putut salva."));
      setBusy(false);
      return;
    }
    const queue = audiences.flatMap((aud) => (picked[aud] || []).map((file) => ({ aud, file })));
    const failed = [];
    if (queue.length && !id) failed.push(...queue.map((q) => q.file.name));
    else {
      for (const [i, { aud, file }] of queue.entries()) {
        setProgress(queue.length === 1 ? `Se încarcă ${file.name}…` : `Se încarcă ${file.name} (${i + 1} din ${queue.length})…`);
        try {
          await uploadFile(`/admin/portal/library/items/${id}/uploads`, file,
            { completeBase: "/admin/portal/library/files", extra: { audience: aud } });
        } catch (err) {
          failed.push(`${file.name} (${uploadErrorText(err, apiErrorMessage)})`);
        }
      }
    }
    if (queue.length) {
      try { next = (await api.get("/admin/portal/library")).data; } catch { /* the saved view will do */ }
    }
    setProgress("");
    setBusy(false);
    if (failed.length) {
      toast.error(`Materialul e salvat, dar nu s-a încărcat: ${failed.join(", ")}. Încarcă-l din bibliotecă, la material.`);
    } else {
      toast.success(queue.length ? `Salvat, cu ${queue.length === 1 ? "fișierul încărcat" : `${queue.length} fișiere încărcate`}.`
        : isNew ? "Materialul e în bibliotecă." : "Salvat.");
    }
    onSaved(next);
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{data.kinds[kind].label}{editing.lesson ? ` · Lecția ${editing.lesson}` : ""}</DialogTitle>
          <DialogDescription>
            {isNew ? "Alege fișierele aici; se încarcă la salvare." : "Fiecare schimbare salvată e o versiune nouă."}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <label className="grid gap-1 text-sm">
            <span className="font-semibold text-[#373A45]">Titlul</span>
            <input className={field} value={title} onChange={(e) => setTitle(e.target.value)} maxLength={160} data-testid="library-title" />
          </label>
          <label className="grid gap-1 text-sm">
            <span className="font-semibold text-[#373A45]">{isTask ? "Cerința" : "Descrierea (opțional)"}</span>
            <FormattedTextarea className={field} rows={isTask ? 8 : 3} value={description} onChange={setDescription}
              maxLength={isTask ? 8000 : 4000} data-testid="library-description"
              placeholder={isTask ? "Ce are de făcut cursantul, pas cu pas, și ce predă la final." : ""} />
          </label>
          {isTask && (
            <div className="grid gap-3 rounded-xl bg-[#F7F5F9] p-3 sm:grid-cols-2" data-testid="library-task-fields">
              <label className="grid gap-1 text-sm">
                <span className="font-semibold text-[#373A45]">Ore asincron</span>
                <input className={field} inputMode="decimal" value={task.hours}
                  onChange={(e) => setTask({ ...task, hours: e.target.value })} />
                <span className="text-xs text-[#6B7280]">În programă, lecția are {hoursText(lessonHours)}.</span>
              </label>
              <label className="grid gap-1 text-sm">
                <span className="font-semibold text-[#373A45]">Componenta</span>
                <select className={field} value={task.component} onChange={(e) => setTask({ ...task, component: e.target.value })}>
                  {Object.entries(data.assignments.component_labels).map(([k, label]) => (
                    <option key={k} value={k}>{label}</option>
                  ))}
                </select>
              </label>
              <label className="grid gap-1 text-sm">
                <span className="font-semibold text-[#373A45]">Termenul</span>
                <span className="flex items-center gap-2">
                  <input className={`${field} w-20`} type="number" min={1} max={30} value={task.due_days}
                    onChange={(e) => setTask({ ...task, due_days: e.target.value })} />
                  <span className="text-xs text-[#6B7280]">zile după sesiunea lecției, ora 23:59</span>
                </span>
              </label>
              <div className="grid content-start gap-2 text-sm">
                <label className="flex items-center gap-2">
                  <input type="checkbox" className="accent-[#965EA7]" checked={task.required}
                    onChange={(e) => setTask({ ...task, required: e.target.checked })} /> Obligatorie
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" className="accent-[#965EA7]" checked={task.hard_close}
                    onChange={(e) => setTask({ ...task, hard_close: e.target.checked })} /> Nu se mai predă după termen
                </label>
              </div>
            </div>
          )}
          <label className="grid gap-1 text-sm">
            <span className="font-semibold text-[#373A45]">Linkuri (opțional)</span>
            <textarea className={field} rows={2} value={links} onChange={(e) => setLinks(e.target.value)}
              placeholder="Câte unul pe rând. Poți pune un nume înainte: Video | https://…" />
          </label>
          <div className="grid gap-2 text-sm" data-testid="library-dialog-files">
            <span className="font-semibold text-[#373A45]">{isNew ? "Fișierele" : "Adaugă fișiere"}</span>
            <div className={`grid grid-cols-1 gap-3 ${audiences.length > 1 ? "sm:grid-cols-2" : ""}`}>
              {audiences.map((aud) => (
                <div key={aud} className="min-w-0 rounded-lg bg-[#F7F5F9] p-3">
                  <p className="text-xs font-semibold text-[#373A45]">{data.audience_labels[aud]}</p>
                  {(picked[aud] || []).length > 0 && (
                    <ul className="mt-1 grid gap-1">
                      {picked[aud].map((f, i) => (
                        <li key={`${f.name}-${i}`} className="flex items-start gap-2">
                          <span className="min-w-0 [overflow-wrap:anywhere]">{f.name}</span>
                          <span className="mt-0.5 shrink-0 whitespace-nowrap text-xs text-[#6B7280]">{formatBytes(f.size)}</span>
                          <button type="button" onClick={() => unpick(aud, i)} disabled={busy} aria-label={`Scoate ${f.name}`}
                            className="ml-auto shrink-0 rounded p-0.5 text-[#9CA3AF] hover:text-[#a33a32]"><X size={13} /></button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <label className={`${quiet} mt-2 cursor-pointer`}>
                    <Upload size={13} /> {uploadLabel(kind, aud)}
                    <input type="file" multiple className="sr-only" disabled={busy} accept={acceptFor(kind, aud)}
                      onChange={(e) => { pick(aud, e.target.files); e.target.value = ""; }}
                      data-testid={`library-dialog-pick-${aud}`} />
                  </label>
                </div>
              ))}
            </div>
            <span className="text-xs text-[#6B7280]">Cel mult 50 MB pe fișier.</span>
          </div>
        </div>
        <DialogFooter className="items-center">
          {progress && <span className="mr-auto text-xs text-[#6B7280]" role="status">{progress}</span>}
          <button type="button" onClick={onClose} disabled={busy}
            className="rounded-full border border-[#E5E7EB] px-4 py-2 text-sm font-semibold text-[#373A45]">Renunță</button>
          <button type="button" onClick={save} disabled={busy || !title.trim()} data-testid="library-save"
            className="rounded-full bg-[#965EA7] px-4 py-2 text-sm font-semibold text-white hover:bg-[#7A4986] disabled:opacity-50">
            {busy && <Loader2 size={14} className="mr-1.5 inline animate-spin" />}Salvează
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function defaultTitle(editing, data) {
  if (!editing.lesson) return data.kinds[editing.kind].label;
  const les = data.lessons.find((x) => x.n === editing.lesson);
  return `${data.kinds[editing.kind].label} · Lecția ${editing.lesson}${les ? `: ${les.name}` : ""}`.slice(0, 160);
}
